import { useMemo } from 'react'

import { endOfDay, max as maxDate, startOfDay, startOfMonth } from 'date-fns'
import { useFormContext, useWatch } from 'react-hook-form'

import { useGetAllocationStabilityReallocatedJobsOverTimeQuery } from 'generated/graphql'

import { ALLOCATION_STABILITY_EARLIEST_DATE } from '../constants/AllocationStability'
import { resolveMetricsPeriod } from '../constants/periods'
import { AllocationStabilityFilterValues } from './AllocationStabilityFilterCriteria'

export interface AllocationStabilityPoint {
  date: string
  count: number
  totalJobs: number
  percentage: number
}

export interface AllocationStabilityData {
  daily: AllocationStabilityPoint[]
  summaryCount: number
  summaryTotal: number
  summaryPercentage: number
  loading: boolean
}

export const useAllocationStabilityData = (): AllocationStabilityData => {
  const { control } = useFormContext<AllocationStabilityFilterValues>()

  const [period, customStart, customEnd, selectedServiceCodes, vehicleType] = useWatch({
    control,
    name: ['period', 'customStart', 'customEnd', 'selectedServiceCodes', 'vehicleType'],
  })

  const isCustom = period === 'custom'
  const now = new Date()

  const { start: rawStart, end } = isCustom
    ? {
        start: customStart ? startOfDay(customStart) : startOfMonth(now),
        end: customEnd ? endOfDay(customEnd) : endOfDay(now),
      }
    : resolveMetricsPeriod(period)

  const start = rawStart ? maxDate([rawStart, ALLOCATION_STABILITY_EARLIEST_DATE]) : null
  const hasValidRange = start != null && end != null && start <= end

  const { data, loading } = useGetAllocationStabilityReallocatedJobsOverTimeQuery({
    variables: {
      input: {
        start: start ?? now,
        end: end ?? now,
        vehicleType,
        xeroCodeIds: selectedServiceCodes?.length
          ? selectedServiceCodes.map((option) => Number(option.value))
          : null,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !hasValidRange,
  })

  return useMemo(() => {
    const series = data?.allocationStabilityReallocatedJobsOverTime

    const daily: AllocationStabilityPoint[] = (series?.daily ?? []).map((point) => ({
      date: point.date,
      count: point.reallocatedCount,
      totalJobs: point.totalBookings,
      percentage: point.percentage,
    }))

    return {
      daily,
      summaryCount: series?.summaryReallocatedCount ?? 0,
      summaryTotal: series?.summaryTotalBookings ?? 0,
      summaryPercentage: series?.summaryPercentage ?? 0,
      loading,
    }
  }, [data, loading])
}
